import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { BundleDTO, CompanyDataBundleUsageDTO, CompanyDataBundleUsageDTO_EmptyObject } from 'src/app/core/models/system/widget/widget-company-data-bundle-usage.dto';
import { catchError, combineLatest, debounceTime, distinctUntilChanged, firstValueFrom, Observable, of, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import { ConfigSystemParams } from 'src/app/core/consts';
import { ConfigSystemService } from 'src/app/core/services/utelize/system/config-system/config-system-service';
import { CountriesService } from 'src/app/core/services/utelize/system/mapping/countries-service';
import { CountryRiskLevelDTO } from 'src/app/core/models/system/riskline/riskline-country-risk-level.dto';
import { EsimLocationDTO } from 'src/app/core/models/system/webbing/esim-location.dto';
import { eTravelRiskDataProvider } from 'src/app/core/enums';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import jsVectorMap from 'jsvectormap';
import 'jsvectormap/dist/maps/world.js';
import { OurDate } from 'src/app/core/interfaces/general/our-date';
import { populateOurDate } from 'src/app/core/functions-generic/functions-generic-date';
import { RisklineCountryRiskLevelsService } from 'src/app/core/services/riskline/riskline-country-risk-levels.service';
import { SignalsAccessService } from 'src/app/core/services/utelize/access/signals/signals-access.service';
import { TravelRiskManagementAccessService } from 'src/app/core/services/utelize/access/risk-intelligence/travel-risk-management-access.service';
import { WidgetCompanyCountryUsageSummaryDTO, WidgetCompanyCountryUsageSummaryDTO_EmptyObject } from 'src/app/core/models/system/widget/widget-company-country-usage-summary.dto';
import { WidgetCountryUsageSummaryDTO } from 'src/app/core/models/system/widget/widget-country-usage-summary.dto';
import { WidgetService } from 'src/app/core/services/utelize/general/widget-service';
import { WidgetZoneUsageSummaryDTO } from 'src/app/core/models/system/widget/widget-zone-usage-summary.dto';

const REGION_LABEL_OTHER="Other";

export interface RegionStat {
  label: string;
  value: number;
}

export interface ZoneCost {
  cost     : string;
  countries: string;
  sims     : string;
  subtitle : string;
  title    : string;
  usage    : string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-widget-row-usage-dashboard-distribution',
  templateUrl: './widget-row-usage-dashboard-distribution.component.html',
  styleUrls: ['./widget-row-usage-dashboard-distribution.component.scss']
})

export class WidgetRowUsageDashboardDistributionComponent implements AfterViewInit, OnDestroy, OnInit {

  @Input() allowContainerClose: boolean | undefined;
  @Input() containerTitle     : string  | undefined;
  @Input() marginBottom       : string  | undefined="mb-0";
  @Input() useContainer       : boolean | undefined;

  @Output() containerClosed       : EventEmitter<void>   =new EventEmitter<void>();
  @Output() rightPanelMovedChange : EventEmitter<boolean>=new EventEmitter<boolean>();

  @ViewChild('mapContainer', { static: false }) mapContainer!      : ElementRef<HTMLElement>;
  @ViewChild('rightPanel',   { static: true  }) rightPanelTemplate!: TemplateRef<unknown>;

  get showRegionHighlightOnMapHover() { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.WidgetRow_UsageDashboard_ShowRegionHighlightOnMapHover, false); }
  get showRiskColourMap            () { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.WidgetRow_UsageDashboard_ShowRiskColourMap,             false); }

  private alpha2ById              : Map<number, string>=new Map();
  private continentsByCode        : Map<string, string>=new Map();
  private countriesByCode         : Map<string, WidgetCountryUsageSummaryDTO>=new Map();
  private countryNamesByCode      : Map<string, string>=new Map();
  private destroy$                =new Subject<void>();
  private filterCompanyID         : number  | undefined;
  private filterDate              : OurDate | undefined;
  private mapInstance             : any | undefined;
  //RBW_20260710 - The jsVectorMap world map draws Kosovo and N. Cyprus as separate regions with placeholder codes (_0/_1) rather than ISO codes, so they show the raw code on hover and never highlight with their parent country. Alias each to the ISO code the backend usage data actually uses. (Somaliland/_2 is fully merged into Somalia instead - see fuseSomalilandIntoSomalia)
  private readonly mapRegionAliases: { code: string; name: string; parent: string }[]=[
    { code: "_0", name: "Cyprus", parent: "CY" },
    { code: "_1", name: "Kosovo", parent: "XK" }
  ];
  private mapResizeFrame          : number | undefined;
  private mapResizeObserver       : ResizeObserver | undefined;
  private mapWindowResizeHandler  : (() => void) | undefined;
  private regionMouseLeaveHandler : ((event: MouseEvent) => void) | undefined;
  private regionMouseMoveHandler  : ((event: MouseEvent) => void) | undefined;
  private refreshNonce            : number=0;
  private riskMapLocations        : EsimLocationDTO[]=[];
  private risklineLicensed        : boolean=false;
  private selection$              =new Subject<{ companyId: number; date: { year: number; month: number; day: number }; nonce: number }>();
  private sureSimLevelsLicensed   : boolean=false;
  private zoomInClickHandler      : ((event: Event) => void) | undefined;
  private zoomOutClickHandler     : ((event: Event) => void) | undefined;

  public activeCountries         : number=0;
  public activeCountryCodes      : string[]=[];
  public activeSimsLocated       : number=0;
  public activeSimsTotal         : number=0;
  public hasUnknownLocation      : boolean=false;
  public bundles                 : BundleDTO[]=[];
  public expandContainer         : boolean=true;
  public hasBundles              : boolean=false;
  public hoveredCountry          : WidgetCountryUsageSummaryDTO | undefined;
  public hoveredCountryCode      : string="";
  public hoveredRegionLabel      : string="";
  public isReseller              : boolean=false;
  public mapMode                 : "pending" | "risk" | "vector"="vector";
  public mapCompanyId            : number | undefined;
  public mapRiskSourceID         : eTravelRiskDataProvider | undefined;
  public outOfBundleTotalCost    : string="£0.00";
  public outOfBundleTotalUsageGB : number=0;
  public outOfBundleZones        : WidgetZoneUsageSummaryDTO[]=[];
  public singleBundleZoneUsage   : WidgetZoneUsageSummaryDTO[]=[];
  public rightPanelMoved         : boolean=false;
  public riskMapCountries        : number=0;
  public riskMapRegionStats      : RegionStat[]=[
    { label: "Americas",         value: 0 },
    { label: "Europe",           value: 0 },
    { label: "Asia",             value: 0 },
    { label: REGION_LABEL_OTHER, value: 0 },
    { label: "Not Located",      value: 0 }
  ];
  public riskMapSimsLocated      : number=0;
  public riskMapSimsTotal        : number=0;
  public regionStats             : RegionStat[]=[
    { label: "Americas", value: 0 },
    { label: "Europe",   value: 0 },
    { label: "Asia",     value: 0 },
    { label: "Other",    value: 0 }
  ];
  public tooltipVisible    : boolean=false;
  public tooltipX          : number=0;
  public tooltipY          : number=0;
  public visibleContainer  : boolean=true;
  public zoneCosts         : ZoneCost[]=[];
  
  public readonly eTravelRiskDataProvider       =eTravelRiskDataProvider;
  public readonly showRiskMapDisclaimer: boolean=false;

  constructor(
    private changeDetectorRef                : ChangeDetectorRef,
    private configSystemService              : ConfigSystemService,
    private countriesService                 : CountriesService,
    private exceptionsService                : ExceptionsService,
    private ngZone                           : NgZone,
    private risklineCountryRiskLevelsService : RisklineCountryRiskLevelsService,
    private signalsAccessService             : SignalsAccessService,
    private travelRiskManagementAccessService: TravelRiskManagementAccessService,
    private widgetService                    : WidgetService
  ) {

  }

  public ngOnInit(): void {
    try {
      this.initSelectionStream();
      this.loadCountryNames();

      this.mapMode="vector";

      if (this.showRiskColourMap)
        this.resolveRiskMapSource();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public get hoveredCountryDisplayName(): string {
    if (this.hoveredCountry?.country)
      return this.hoveredCountry.country;

    const lookup=this.countryNamesByCode.get(this.hoveredCountryCode);

    return lookup ?? this.hoveredCountryCode;
  }

  public ngAfterViewInit(): void {
    try {
      if (this.mapMode==="vector")
        this.initVectorMap();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngOnDestroy(): void {
    try {
      this.destroyVectorMap();

      this.destroy$.next();
      this.destroy$.complete();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#region Private Methods

  private applyCompanyBundleDetails(data: BundleDTO[]): void {
    try {
      //RBW_20260508 - hasBundles now tracks the actual bundle count for the company. The previous bundleCosts>0 check missed bundles that simply had no usage yet
      this.hasBundles=(data ?? []).length>0;
      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private applyCompanyDataBundleUsage(data: CompanyDataBundleUsageDTO): void {
    try {
      const totalCostNum=data?.totalOutOfBundleCost ?? 0;
      const totalCostSym=(data?.totalOutOfBundleCostWithSymbol ?? "").trim();

      this.bundles                =data?.bundles          ?? [];
      this.outOfBundleZones       =data?.outOfBundleZones ?? [];
      this.outOfBundleTotalCost   =totalCostSym.length>0 ? totalCostSym : "£"+totalCostNum.toFixed(2);
      this.outOfBundleTotalUsageGB=data?.totalOutOfBundleUsageGB ?? 0;

      if (this.bundles.length===1 && this.bundles[0].bundleId!=null)
        this.fetchSingleBundleZoneUsage(this.bundles[0].bundleId);
      else
        this.singleBundleZoneUsage=[];

      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private fetchSingleBundleZoneUsage(bundleId: number): void {
    try {
      if (this.filterCompanyID===undefined || this.filterCompanyID===null)
        return;

      const userId=-1;
      const year  =<number>(this.filterDate?.year  ?? 1);
      const month =<number>(this.filterDate?.month ?? 1);
      const day   =<number>(this.filterDate?.day   ?? 1);

      this.widgetService.getZoneUsageBundleSummary(userId, this.filterCompanyID, bundleId, year, month, day)
        .pipe(
          catchError(error => {
            this.exceptionsService.handleException(new Error(this.exceptionsService.formatHttpError(error)));
            return of(<WidgetZoneUsageSummaryDTO[]>[]);
          }),
          takeUntil(this.destroy$)
        )
        .subscribe(zones => {
          this.singleBundleZoneUsage=zones ?? [];
          this.changeDetectorRef.markForCheck();
        });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public parseUsageGB(usage: string | undefined | null): number {
    try {
      if (!usage)
        return 0;

      const cleaned=usage.replace(/[^0-9.]/g, "");

      return parseFloat(cleaned) || 0;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 0;
    }
  }

  public zoneUsagePercent(zone: WidgetZoneUsageSummaryDTO): number {
    try {
      const bundle=this.bundles[0];

      if (!bundle || !bundle.totalAllowance || bundle.totalAllowance<=0)
        return 0;

      const used=this.parseUsageGB(zone.totalUsage);

      return Math.min(100, Math.round((used / bundle.totalAllowance) * 100));
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 0;
    }
  }

  public zoneColours: string[]=["#7269ef", "#10b981", "#06b6d4", "#f59e0b", "#ec4899", "#8b5cf6"];

  public zoneColour(index: number): string {
    return this.zoneColours[index % this.zoneColours.length];
  }

  public bundleProgressClass(bundle: BundleDTO): string {
    try {
      const pct=this.bundlePercentage(bundle);

      if (pct>=90) return "bundle-progress-critical";
      if (pct>=75) return "bundle-progress-high";
      if (pct>=50) return "bundle-progress-mid";
      return "bundle-progress-low";
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "bg-secondary";
    }
  }

  public bundleTextClass(bundle: BundleDTO): string {
    try {
      const pct=this.bundlePercentage(bundle);

      if (pct>=90) return "bundle-text-critical";
      if (pct>=75) return "bundle-text-high";
      if (pct>=50) return "bundle-text-mid";
      return "bundle-text-low";
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "text-muted";
    }
  }

  public bundlePercentage(bundle: BundleDTO): number {
    try {
      const total=bundle?.totalAllowance ?? 0;
      const used =bundle?.used           ?? 0;

      if (total<=0)
        return 0;

      return Math.round((used / total) * 100);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return 0;
    }
  }

  //RBW_20260710 - Render data quantities at the most sensible unit. Values arrive in the bundle's dataWeightCode (e.g. a pool bundle of 1,000,000 MB). Scale 1024-based up to the largest unit that keeps the number >= 1 (so 1,000,000 MB shows as 976.6 GB, not a sub-1 TB figure) and down for small values, so used/remaining/total can each display a different weight
  public formatDataWeight(value: number | null | undefined, sourceUnitCode: string | undefined): string {
    try {
      const units =["KB", "MB", "GB", "TB"];
      const source=(sourceUnitCode ?? "GB").toUpperCase();

      let idx=units.indexOf(source);

      if (idx<0)
        idx=units.indexOf("GB");

      let amount=value ?? 0;

      //RBW_20260710 - Zero has no scale of its own, so anchor it to MB (the base usage unit) rather than leaving it in the source unit - avoids "0 GB" sitting next to MB-sized siblings
      if (amount===0)
        return "0 MB";

      while (amount>=1024 && idx<units.length-1) {
        amount/=1024;
        idx++;
      }

      while (amount>0 && amount<1 && idx>0) {
        amount*=1024;
        idx--;
      }

      return amount.toLocaleString("en-GB", { maximumFractionDigits: 1, minimumFractionDigits: 0 })+" "+units[idx];
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "";
    }
  }

  public bundleResetLabel(bundle: BundleDTO): string {
    try {
      const period=bundle?.resetPeriod ?? "monthly";

      return "Resets "+period;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "";
    }
  }

  public bundleResetDateLabel(bundle: BundleDTO): string {
    try {
      if (!bundle?.resetDate)
        return "";

      const d=new Date(bundle.resetDate);

      if (isNaN(d.getTime()))
        return "";

      const day  =d.getDate();
      const suffix=day>=11 && day<=13 ? "th" : ["th","st","nd","rd","th","th","th","th","th","th"][day % 10];
      const month=d.toLocaleString("en-GB", { month: "short" });
      const year =d.getFullYear();

      return day+suffix+" "+month+" "+year;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "";
    }
  }

  private applyCompanyCountryUsageSummary(data: WidgetCompanyCountryUsageSummaryDTO): void {
    try {
      const countries=data?.countries ?? [];

      this.activeCountryCodes=countries.map(c => (c.code ?? "").toUpperCase()).filter(code => code.length>0);
      this.activeCountries   =this.activeCountryCodes.length;

      this.countriesByCode=new Map();
      countries.forEach(c => {
        const code=(c.code ?? "").toUpperCase();

        if (code.length>0)
          this.countriesByCode.set(code, c);
      });

      const simsAmericas=data?.totalSimsAmericas        ?? 0;
      const simsAsia    =data?.totalSimsAsia            ?? 0;
      const simsEurope  =data?.totalSimsEurope          ?? 0;
      const simsOther   =data?.totalSimsOther           ?? 0;
      const simsUnknown =data?.totalSimsUnknownLocation ?? 0;

      this.hasUnknownLocation=(data?.totalSimsUnknownLocation ?? null)!==null;
      this.activeSimsLocated =simsAmericas+simsAsia+simsEurope+simsOther;
      this.activeSimsTotal   =this.activeSimsLocated+simsUnknown;

      this.regionStats=[
        { label: "Americas", value: simsAmericas },
        { label: "Europe",   value: simsEurope   },
        { label: "Asia",     value: simsAsia     },
        { label: "Other",    value: simsOther    }
      ];

      if (this.hasUnknownLocation)
        this.regionStats.push({ label: "Not Connected", value: simsUnknown });

      this.rebuildMap();
      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private applyZoneUsageSummary(data: WidgetZoneUsageSummaryDTO[]): void {
    try {
      this.zoneCosts=(data ?? []).map(dto => this.mapDtoToZoneCost(dto));

      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private buildMap(): void {
    try {
      if (!this.mapContainer?.nativeElement)
        return;

      this.mapInstance=new jsVectorMap({
        backgroundColor    : "transparent",
        draggable          : true,
        map                : "world",
        regionsSelectable  : false,
        regionStyle        : {
          hover         : { fill: "#bcbed4", "fill-opacity": 1.0 },
          initial       : { fill: "#e3e5ee", "fill-opacity": 1.0, stroke: "#ffffff", "stroke-width": 0.5 },
          selected      : { fill: "#7269ef", "fill-opacity": 1.0 },
          selectedHover : { fill: "#5a52d4", "fill-opacity": 1.0 }
        },
        selectedRegions    : this.getSelectedRegionCodes(),
        selector           : this.mapContainer.nativeElement,
        showTooltip        : false,
        zoomButtons        : true,
        zoomOnScroll       : false
      });

      this.splitFrenchGuiana();
      this.fuseSomalilandIntoSomalia();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private fuseSomalilandIntoSomalia(): void {
    try {
      const container=this.mapContainer?.nativeElement;

      if (!container)
        return;

      const soPath=<SVGPathElement | null>container.querySelector('path[data-code="SO"]');
      const slPath=<SVGPathElement | null>container.querySelector('path[data-code="_2"]');

      if (!soPath || !slPath)
        return;

      //RBW_20260710 - Merge the Somaliland shape (placeholder region _2) into the Somalia (SO) path so the map treats them as one country: a single hover target, one highlight, labelled Somalia. Somaliland has no ISO code of its own - it is de jure part of Somalia. SVG sub-paths each start with "M", so the geometry simply concatenates
      soPath.setAttribute("d", (soPath.getAttribute("d") ?? "")+(slPath.getAttribute("d") ?? ""));
      slPath.remove();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private splitFrenchGuiana(): void {
    try {
      const container=this.mapContainer?.nativeElement;

      if (!container)
        return;

      const frPath=<SVGPathElement | null>container.querySelector('path[data-code="FR"]');

      if (!frPath || container.querySelector('path[data-code="GF"]'))
        return;

      //RBW_20260710 - The bundled jsVectorMap world map has no French Guiana region - its outline is baked into the France (FR) path, so hovering it reports "France". FR is drawn as separate "M...Z" sub-shapes (mainland, Corsica, French Guiana); overlay the southern-hemisphere sub-shape (start y > 220) as its own GF region so it labels correctly. Mainland France, Corsica and the GB path (Northern Ireland) are left untouched
      const subPaths=(frPath.getAttribute("d") ?? "").split("M").filter(s => s.trim().length>0).map(s => "M"+s);
      const guiana  =subPaths.filter(s => {
        const startY=s.match(/^M\s*[-\d.]+\s*,\s*([-\d.]+)/);

        return startY ? parseFloat(startY[1])>220 : false;
      });

      if (guiana.length===0)
        return;

      const gfPath=<SVGPathElement>frPath.cloneNode(false);

      //RBW_20260710 - GF is a DOM-only region jsVectorMap doesn't know about, so it never applies the hover/selected fills its own regions get. Drive the fills manually to match the regionStyle colours in buildMap (selected/selectedHover when the country has active SIMs, otherwise initial/hover)
      const gfActive =this.activeCountryCodes.includes("GF");
      const baseFill =gfActive ? "#7269ef" : "#e3e5ee";
      const hoverFill=gfActive ? "#5a52d4" : "#bcbed4";

      gfPath.setAttribute("d", guiana.join(""));
      gfPath.setAttribute("data-code", "GF");
      gfPath.setAttribute("fill", baseFill);

      gfPath.addEventListener("mouseenter", () => gfPath.setAttribute("fill", hoverFill));
      gfPath.addEventListener("mouseleave", () => gfPath.setAttribute("fill", baseFill));

      frPath.parentNode?.insertBefore(gfPath, frPath.nextSibling);

      if (!this.countryNamesByCode.has("GF"))
        this.countryNamesByCode.set("GF", "French Guiana");
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private getSelectedRegionCodes(): string[] {
    try {
      const codes=[...this.activeCountryCodes];

      //RBW_20260710 - A SIM in e.g. Somaliland is coded SO by the backend, so also select the placeholder region (_2) whenever its parent country is active - otherwise the disputed-territory shape stays unhighlighted
      this.mapRegionAliases.forEach(alias => {
        if (this.activeCountryCodes.includes(alias.parent) && !codes.includes(alias.code))
          codes.push(alias.code);
      });

      return codes;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return this.activeCountryCodes;
    }
  }

  private resolveRegionLabel(code: string): string {
    try {
      if (!this.showRegionHighlightOnMapHover)
        return "";

      const bucket=(this.continentsByCode.get(this.resolveRegionParent(code)) ?? "").trim();

      if (bucket.length===0)
        return "";

      return this.regionStats.some(r => r.label===bucket) ? bucket : REGION_LABEL_OTHER;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "";
    }
  }

  private resolveRegionParent(code: string): string {
    try {
      const alias=this.mapRegionAliases.find(a => a.code===code);

      return alias ? alias.parent : code;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return code;
    }
  }

  private loadCountryNames(): void {
    try {
      this.countriesService.get()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next : countries => {
            this.alpha2ById        =new Map();
            this.continentsByCode  =new Map();
            this.countryNamesByCode=new Map();

            (countries ?? []).forEach(c => {
              const code     =(c.alpha2Code ?? "").toUpperCase();
              const continent=(c.continent  ?? "").trim();

              if (code.length>0) {
                this.alpha2ById.set(c.id, code);
                this.countryNamesByCode.set(code, c.countryName ?? c.alias ?? code);

                if (continent.length>0)
                  this.continentsByCode.set(code, continent);
              }
            });

            //RBW_20260710 - Give the placeholder-coded regions (_0/_1/_2) their parent country's name so the tooltip reads "Somalia" etc. instead of the raw code
            this.mapRegionAliases.forEach(alias => {
              if (!this.countryNamesByCode.has(alias.code))
                this.countryNamesByCode.set(alias.code, this.countryNamesByCode.get(alias.parent) ?? alias.name);

              const parentContinent=this.continentsByCode.get(alias.parent);

              if (!this.continentsByCode.has(alias.code) && parentContinent)
                this.continentsByCode.set(alias.code, parentContinent);
            });
            
            this.buildRiskMapSummary();
            this.changeDetectorRef.markForCheck();
          },
          error: error => {
            this.exceptionsService.handleException(new Error(this.exceptionsService.formatHttpError(error)));
          }
        });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private setupRegionHoverListeners(): void {
    try {
      if (!this.mapContainer?.nativeElement)
        return;

      const container=this.mapContainer.nativeElement;

      this.regionMouseMoveHandler=(event: MouseEvent) => {
        const target=<Element | null>event.target;
        const region=target?.closest(".jvm-region");

        if (region instanceof Element) {
          const code=(region.getAttribute("data-code") ?? "").toUpperCase();

          if (code!==this.hoveredCountryCode) {
            this.hoveredCountryCode=code;
            this.hoveredCountry    =this.countriesByCode.get(this.resolveRegionParent(code));
            this.hoveredRegionLabel=this.resolveRegionLabel(code);
          }

          const rect=container.getBoundingClientRect();
          this.tooltipX     =event.clientX - rect.left + 12;
          this.tooltipY     =event.clientY - rect.top  + 12;
          this.tooltipVisible=true;

          this.changeDetectorRef.markForCheck();
        }
        else if (this.tooltipVisible) {
          this.tooltipVisible    =false;
          this.hoveredCountry    =undefined;
          this.hoveredCountryCode="";
          this.hoveredRegionLabel="";
          this.changeDetectorRef.markForCheck();
        }
      };

      this.regionMouseLeaveHandler=() => {
        this.tooltipVisible    =false;
        this.hoveredCountry    =undefined;
        this.hoveredCountryCode="";
        this.hoveredRegionLabel="";
        this.changeDetectorRef.markForCheck();
      };

      container.addEventListener("mousemove",  this.regionMouseMoveHandler);
      container.addEventListener("mouseleave", this.regionMouseLeaveHandler);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private setupMapResizeObserver(): void {
    try {
      if (!this.mapContainer?.nativeElement)
        return;

      //RBW_20260617 - jsVectorMap does NOT refit on its own (no internal window-resize handler), and its SVG is sized in fixed pixels - so on any size change we must call updateSize(). A window resize listener handles live window/corner dragging; the ResizeObserver handles container-only changes (responsive column reflow, container expand/collapse) that do not fire a window resize
      this.mapWindowResizeHandler=() => this.scheduleMapRefit();
      window.addEventListener("resize", this.mapWindowResizeHandler);

      if (typeof ResizeObserver!=="undefined") {
        this.mapResizeObserver=new ResizeObserver(() => this.scheduleMapRefit());
        this.mapResizeObserver.observe(this.mapContainer.nativeElement);
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private scheduleMapRefit(): void {
    try {
      if (this.mapResizeFrame!==undefined)
        cancelAnimationFrame(this.mapResizeFrame);

      this.mapResizeFrame=requestAnimationFrame(() => {
        try {
          this.mapResizeFrame=undefined;

          if (this.mapInstance && typeof this.mapInstance.updateSize==="function")
            this.mapInstance.updateSize();
        }
        catch(e) {
          this.exceptionsService.handleException(<Error>e);
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private teardownMapResizeObserver(): void {
    try {
      if (this.mapResizeFrame!==undefined) {
        cancelAnimationFrame(this.mapResizeFrame);
        this.mapResizeFrame=undefined;
      }

      if (this.mapWindowResizeHandler) {
        window.removeEventListener("resize", this.mapWindowResizeHandler);
        this.mapWindowResizeHandler=undefined;
      }

      if (this.mapResizeObserver) {
        this.mapResizeObserver.disconnect();
        this.mapResizeObserver=undefined;
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private setupZoomInFocus(): void {
    try {
      if (!this.mapContainer?.nativeElement)
        return;

      const zoomInBtn =<HTMLElement | null>this.mapContainer.nativeElement.querySelector(".jvm-zoomin");
      const zoomOutBtn=<HTMLElement | null>this.mapContainer.nativeElement.querySelector(".jvm-zoomout");

      const buildHandler=(zoomIn: boolean): ((event: Event) => void) => {
        return (event: Event) => {
          try {
            if (!this.mapInstance)
              return;

            event.stopImmediatePropagation();
            event.preventDefault();

            const zoomStep       =<number>(this.mapInstance.params?.zoomStep ?? 1.5);
            const baseScale      =<number>(this.mapInstance._baseScale ?? 1);
            const currentUserScale=baseScale>0 ? <number>this.mapInstance.scale / baseScale : 1;
            const targetUserScale=zoomIn ? currentUserScale * zoomStep : currentUserScale / zoomStep;

            if (typeof this.mapInstance.setFocus==="function") {
              this.mapInstance.setFocus({
                animate: true,
                coords : [54, -2],
                scale  : targetUserScale
              });
            }
          }
          catch(e) {
            this.exceptionsService.handleException(<Error>e);
          }
        };
      };

      if (zoomInBtn) {
        this.zoomInClickHandler=buildHandler(true);
        zoomInBtn.addEventListener("click", this.zoomInClickHandler, true);
      }

      if (zoomOutBtn) {
        this.zoomOutClickHandler=buildHandler(false);
        zoomOutBtn.addEventListener("click", this.zoomOutClickHandler, true);
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private teardownZoomInFocus(): void {
    try {
      if (!this.mapContainer?.nativeElement)
        return;

      const zoomInBtn =<HTMLElement | null>this.mapContainer.nativeElement.querySelector(".jvm-zoomin");
      const zoomOutBtn=<HTMLElement | null>this.mapContainer.nativeElement.querySelector(".jvm-zoomout");

      if (zoomInBtn && this.zoomInClickHandler) {
        zoomInBtn.removeEventListener("click", this.zoomInClickHandler, true);
      }

      if (zoomOutBtn && this.zoomOutClickHandler) {
        zoomOutBtn.removeEventListener("click", this.zoomOutClickHandler, true);
      }

      this.zoomInClickHandler =undefined;
      this.zoomOutClickHandler=undefined;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private teardownRegionHoverListeners(): void {
    try {
      if (!this.mapContainer?.nativeElement)
        return;

      const container=this.mapContainer.nativeElement;

      if (this.regionMouseMoveHandler) {
        container.removeEventListener("mousemove", this.regionMouseMoveHandler);
        this.regionMouseMoveHandler=undefined;
      }

      if (this.regionMouseLeaveHandler) {
        container.removeEventListener("mouseleave", this.regionMouseLeaveHandler);
        this.regionMouseLeaveHandler=undefined;
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private rebuildMap(): void {
    try {
      if (!this.mapContainer?.nativeElement)
        return;

      this.teardownZoomInFocus();

      if (this.mapInstance) {
        try {
          this.mapInstance.destroy();
        }
        catch {
          this.mapContainer.nativeElement.innerHTML="";
        }
        this.mapInstance=undefined;
      }

      this.buildMap();
      this.setupZoomInFocus();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private destroyVectorMap(): void {
    try {
      this.teardownRegionHoverListeners();
      this.teardownZoomInFocus();
      this.teardownMapResizeObserver();

      if (this.mapInstance) {
        try {
          this.mapInstance.destroy();
        }
        catch {

        }
        this.mapInstance=undefined;
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private initVectorMap(): void {
    try {
      setTimeout(() => {
        this.buildMap();
        this.setupRegionHoverListeners();
        this.setupZoomInFocus();
        this.setupMapResizeObserver();
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private async resolveRiskMapSource(): Promise<void> {
    try {
      await Promise.all([this.signalsAccessService.ensureInitialised(), this.travelRiskManagementAccessService.ensureInitialised()]);

      this.risklineLicensed=this.travelRiskManagementAccessService.isRisklineEnabled()
                            || this.travelRiskManagementAccessService.isTravelAwarenessEnabled();
      
      this.sureSimLevelsLicensed=this.signalsAccessService.isFeatureEnabled() || this.risklineLicensed;

      if (this.risklineLicensed && await this.risklineHasCountryLevels())
        this.mapRiskSourceID=eTravelRiskDataProvider.Riskline;
      else if (this.sureSimLevelsLicensed)
        this.mapRiskSourceID=eTravelRiskDataProvider.SureSIM;
      else
        this.mapRiskSourceID=undefined;

      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.mapRiskSourceID=undefined;
      this.changeDetectorRef.markForCheck();
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private async risklineHasCountryLevels(): Promise<boolean> {
    try {
      const levels: CountryRiskLevelDTO[]=await firstValueFrom(this.risklineCountryRiskLevelsService.getAllRiskLevels());

      return (levels ?? []).some(level => level?.riskLevelID!=undefined);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  private buildRiskMapSummary(): void {
    try {
      const seenIccids : Record<string, boolean>={};
      const countryIsos: Record<string, boolean>={};
      const buckets    : Record<string, number> ={ "Americas": 0, "Europe": 0, "Asia": 0 };
      let   located    : number=0;
      let   notLocated : number=0;
      let   other      : number=0;

      (this.riskMapLocations ?? []).forEach(row => {
        const iccid: string=row?.ICCID ?? "";

        if (iccid.length>0) {
          if (seenIccids[iccid])
            return;

          seenIccids[iccid]=true;
        }

        let countryId: number=row?.CountryId ?? -1;

        if (countryId<-100000)
          countryId=row?.ParentCountryId ?? (countryId+1000000);

        if (countryId<=0) {
          notLocated++;
          return;
        }

        located++;

        const iso      : string | undefined=this.alpha2ById.get(countryId)
                                            ?? (row?.ParentCountryId!=null ? this.alpha2ById.get(row.ParentCountryId) : undefined);
        const continent: string            =iso!=undefined ? (this.continentsByCode.get(iso) ?? "").trim() : "";

        if (iso!=undefined)
          countryIsos[iso]=true;

        if (buckets[continent]!=undefined)
          buckets[continent]++;
        else
          other++;
      });

      this.riskMapCountries  =Object.keys(countryIsos).length;
      this.riskMapSimsLocated=located;
      this.riskMapSimsTotal  =located+notLocated;
      this.riskMapRegionStats=[
        { label: "Americas",         value: buckets["Americas"] },
        { label: "Europe",           value: buckets["Europe"]   },
        { label: "Asia",             value: buckets["Asia"]     },
        { label: REGION_LABEL_OTHER, value: other               },
        { label: "Not Located",      value: notLocated          }
      ];

      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private setRightPanelMoved(moved: boolean): void {
    try {
      if (this.rightPanelMoved===moved)
        return;

      this.rightPanelMoved=moved;
      this.changeDetectorRef.markForCheck();
      this.rightPanelMovedChange.emit(moved);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private initSelectionStream(): void {
    try {
      this.selection$
      .pipe(
        debounceTime(150),
        distinctUntilChanged((a, b) =>
          a.companyId ===b.companyId  &&
          a.date.year ===b.date.year  &&
          a.date.month===b.date.month &&
          a.date.day  ===b.date.day   &&
          a.nonce     ===b.nonce
        ),
        switchMap(selected => {
          const companyId=selected.companyId;
          const year     =selected.date.year;
          const month    =selected.date.month;
          const day      =selected.date.day;
          const userId   =-1; //this.authenticationService.currentUserObject()?.userId ?? 0;

          const safeStart=<T>(observable: Observable<T>, fallback: T) =>
            observable.pipe(
              startWith(fallback),
              catchError(err => {
                this.exceptionsService.handleException(new Error(this.exceptionsService.formatHttpError(err)));
                return of(fallback);
              })
            );

          return combineLatest({
            companyBundleDetails      : safeStart(this.widgetService.getCompanyBundleDetails      (companyId, year, month, day),         <BundleDTO[]>[]),
            companyCountryUsageSummary: safeStart(this.widgetService.getCompanyCountryUsageSummary(userId, companyId, year, month, day), WidgetCompanyCountryUsageSummaryDTO_EmptyObject()),
            companyDataBundleUsage    : safeStart(this.widgetService.getCompanyDataBundleUsage    (userId, companyId, year, month, day), CompanyDataBundleUsageDTO_EmptyObject()),
            zoneUsageSummary          : safeStart(this.widgetService.getZoneUsageSummary          (userId, companyId, year, month, day), <WidgetZoneUsageSummaryDTO[]>[])
          });
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        this.applyCompanyBundleDetails      (result.companyBundleDetails);
        this.applyCompanyCountryUsageSummary(result.companyCountryUsageSummary);
        this.applyCompanyDataBundleUsage    (result.companyDataBundleUsage);
        this.applyZoneUsageSummary          (result.zoneUsageSummary);
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private mapDtoToZoneCost(dto: WidgetZoneUsageSummaryDTO): ZoneCost {
    try {
      const noOfCountries=dto.noOfCountries ?? 0;
      const avgPerSim    =dto.avgCostPerSimWithCurrencySymbol ?? "";
      const subtitle     =noOfCountries+" "+(noOfCountries===1 ? "country" : "countries")+
                          (avgPerSim ? " · Avg "+avgPerSim+"/SIM" : "");

      return {
        cost     : dto.totalCostWithCurrencySymbol ?? "",
        countries: noOfCountries+" countries",
        sims     : (dto.noOfICCIDs ?? 0).toLocaleString(),
        subtitle : subtitle,
        title    : dto.zone ?? "",
        usage    : dto.totalUsage ?? ""
      };
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return { cost: "", countries: "", sims: "", subtitle: "", title: "", usage: "" };
    }
  }

  private triggerFetch(): void {
    try {
      if (this.filterCompanyID===undefined || this.filterCompanyID===null)
        return;

      const date={
        year : <number>(this.filterDate?.year  ?? 1),
        month: <number>(this.filterDate?.month ?? 1),
        day  : <number>(this.filterDate?.day   ?? 1)
      };

      this.selection$.next({ companyId: this.filterCompanyID, date, nonce: this.refreshNonce });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#endregion

  //#region Public Methods

  public switchRiskMapSource(source: eTravelRiskDataProvider): void {
    try {
      const licensed: boolean=source===eTravelRiskDataProvider.Riskline
                              ? this.risklineLicensed
                              : this.sureSimLevelsLicensed;

      if (!licensed) {
        this.exceptionsService.handleStringException('WidgetRowUsageDashboardDistributionComponent.switchRiskMapSource() - This company is not licensed for the '
                                                     +(source===eTravelRiskDataProvider.Riskline ? 'Riskline' : 'SureSIM')+' country risk levels');
        this.changeDetectorRef.markForCheck();
        return;
      }

      this.mapRiskSourceID=source;
      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public setMapView(view: "risk" | "vector"): void {
    try {
      if (this.mapRiskSourceID===undefined || this.mapMode===view)
        return;

      if (this.mapMode==="vector")
        this.destroyVectorMap();

      if (view==="vector")
        this.setRightPanelMoved(false);

      this.mapMode           =view;
      this.hoveredRegionLabel="";
      this.changeDetectorRef.markForCheck();

      if (view==="vector")
        this.initVectorMap();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public onRiskMapCountryHovered(iso: string | undefined): void {
    try {
      const label: string=iso!=undefined ? this.resolveRegionLabel(iso.toUpperCase()) : "";

      if (label===this.hoveredRegionLabel)
        return;

      this.ngZone.run(() => {
        this.hoveredRegionLabel=label;
        this.changeDetectorRef.markForCheck();
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public onRiskMapLocationDataLoaded(rows: EsimLocationDTO[]): void {
    try {
      this.riskMapLocations=rows ?? [];
      this.buildRiskMapSummary();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public onRiskMapSidebarCollapsedChange(collapsed: boolean): void {
    try {
      this.setRightPanelMoved(!collapsed);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public closeContainer(): void {
    try {
      this.setRightPanelMoved(false);
      this.visibleContainer=false;
      this.containerClosed.emit();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public emitSelection(filterCompanyID: number | undefined, filterDate: OurDate | undefined, isReseller: boolean=false): void {
    try {
      this.filterCompanyID=filterCompanyID;
      this.filterDate     =filterDate ?? populateOurDate(this.exceptionsService, Date.now());
      this.isReseller     =isReseller;
      this.mapCompanyId   =filterCompanyID;

      this.changeDetectorRef.markForCheck();
      this.triggerFetch();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public refresh(): void {
    try {
      this.refreshNonce++;
      this.triggerFetch();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public toggleContainerExpansion(): void {
    try {
      this.expandContainer=!this.expandContainer;

      if (!this.expandContainer)
        this.setRightPanelMoved(false);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#endregion
}
